import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/use-language";
import { ORDER_CTAS_ENABLED } from "@/config/orderCtas";
import { INSTALLATION_ENABLED } from "@/config/installation";
import logoWide from "@/assets/logo-wide.webp";
import bmftrLogoDe from "@/assets/bmftr-logo.svg";
import bmftrLogoEn from "@/assets/bmbf-logo-en.webp";


export function Footer() {
  const { t, i18n } = useTranslation();
  const { localizedPath } = useLanguage();
  const bmftrLogo = i18n.language === "de" ? bmftrLogoDe : bmftrLogoEn;

  return (
    <footer className="bg-footer text-footer-foreground">
      <div className="container mx-auto px-4 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="mb-4">
              <img src={logoWide} alt="Praedigo" className="h-6" width="82" height="24" />
            </div>
            <p className="text-sm text-footer-foreground/60 leading-relaxed mb-4">{t("footer.tagline")}</p>
            <p className="text-xs text-footer-foreground/70 mb-3">{t("footer.supportedBy")}</p>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="inline-block bg-white rounded-md p-1">
                <img src={bmftrLogo} alt="Federal Ministry of Research, Technology and Space" className="h-16 w-auto" width="160" height="64" />
              </span>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-footer-foreground/80">
              {t("footer.product")}
            </h3>
            <ul className="space-y-2.5">
              {[
              { label: t("nav.whyPraedigo"), href: "/#diagnostic-gap" },
                { label: t("nav.product"), href: "/product" },
                { label: t("nav.newsEvents"), href: "/comorbidities" },
                { label: t("nav.access"), href: "/praedigo-access" },
                ...(ORDER_CTAS_ENABLED ? [{ label: t("nav.order"), href: "/bestellung" }] : []),
                { label: t("nav.contactUs"), href: "/contact" },
                { label: t("footer.instructionsForUse"), href: "/instructions-for-use" },
                ...(INSTALLATION_ENABLED ? [{ label: t("footer.installationInstructions"), href: "/installation" }] : []),
              ].map((item) => (
                <li key={item.href}>
                  <Link
                    to={localizedPath(item.href)}
                    className="text-sm text-footer-foreground/60 hover:text-footer-foreground transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-footer-foreground/80">
              {t("footer.legal")}
            </h3>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to={localizedPath("/privacy")}
                  className="text-sm text-footer-foreground/60 hover:text-footer-foreground transition-colors"
                >
                  {t("footer.privacyPolicy")}
                </Link>
              </li>
              <li>
                <Link
                  to={localizedPath("/legal")}
                  className="text-sm text-footer-foreground/60 hover:text-footer-foreground transition-colors"
                >
                  {t("footer.legalNotice")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-footer-foreground/80">
              {t("footer.contact")}
            </h3>
            <address className="not-italic text-sm text-footer-foreground/60 space-y-1.5">
              <a
                href="mailto:info@praedigo.com"
                className="flex items-center gap-2 hover:text-footer-foreground transition-colors"
              >
                <Mail size={14} /> info@praedigo.com
              </a>
            </address>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-footer-foreground/10 space-y-4">
          <p className="text-xs text-footer-foreground/70 leading-relaxed max-w-4xl">
            {t("medicalDisclaimer")}
          </p>
          <p className="text-xs text-footer-foreground/60">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
    </footer>
  );
}
